/**
 * Offline storage for work in progress.
 *
 * REWORKED 2026-09-05. The service had no importers, and three of its methods
 * could not work:
 *
 *   `syncToCloud` and `resolveConflict` both called
 *   `getOfflineProjects('')`, which filters on `p.uid === uid` — so an empty
 *   string matched nothing and neither method ever found the project it was
 *   about. `resolveConflict` therefore always returned false.
 *
 *   `resolveConflict('remote')` and `('merge')` set `syncStatus = 'synced'`
 *   and did nothing else. The stale local copy stayed in IndexedDB marked as
 *   synced, ready to be resurrected over the server's version on the next
 *   sync — a data-loss shape rather than a merge. `'merge'` is gone; merging a
 *   child's code is not something to fake, and 'remote' now discards the local
 *   copy as it claims to.
 *
 *   `addToSyncQueue` keyed entries `${projectId}-${Date.now()}` on a store
 *   with a unique keyPath, so two saves in the same millisecond raised a
 *   ConstraintError that was swallowed as `false`.
 *
 * WHAT IT IS FOR. The editor autosaves to Firestore every two seconds. When
 * that write fails — a dropped Chromebook Wi-Fi, which is the normal case in a
 * classroom — the child's work exists only in React state. The draft API below
 * is the durable copy: written on every failed save, read back on the next
 * load, and offered as a recovery if it is newer than the server's version.
 *
 * IndexedDB is the store, with localStorage as a fallback: private browsing and
 * a few managed-device configurations block IDB, and a child on one of those
 * devices should still not lose their work.
 */

import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { createComponentLogger } from '../utils/logger';

const logger = createComponentLogger('OfflineService');

export type SyncStatus = 'synced' | 'pending' | 'conflict' | 'error';

export interface OfflineProject {
  id: string;
  name: string;
  description?: string;
  uid: string;
  syncStatus: SyncStatus;
  lastSyncedAt?: Date;
  localData: Record<string, unknown>;
}

export interface SyncQueueItem {
  id: string;
  projectId: string;
  operation: 'create' | 'update' | 'delete';
  data: Record<string, unknown>;
  timestamp: Date;
  retries: number;
}

/** Monotonic suffix so two queue entries in the same millisecond differ. */
let __queueSeq = 0;
const queueSequence = (): number => ++__queueSeq;

/** localStorage key for a draft, used when IndexedDB is unavailable. */
/**
 * S61: the localStorage draft key was `nsgs_draft_${projectId}` — no viewer in it. A
 * shared classroom device runs many children through one browser, so the next child to
 * open the same project id was handed the previous child's unsaved code. That is
 * standing rule 9b, and the same hole as S12 one storage layer down.
 */
const draftKey = (projectId: string, uid: string) => `nsgs_draft_${uid}_${projectId}`;

const DB_NAME = 'ns_gaming_studio';
const PROJECTS_STORE = 'offlineProjects';
const SYNC_QUEUE_STORE = 'syncQueue';
const DB_VERSION = 1;

class OfflineService {
  private db: IDBDatabase | null = null;
  private isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

  /**
   * Initialize offline storage with IndexedDB
   */
  async initOfflineStorage(): Promise<boolean> {
    if (typeof window === 'undefined' || !window.indexedDB) {
      console.warn('IndexedDB not available');
      return false;
    }

    return new Promise((resolve) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open IndexedDB');
        resolve(false);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.setupOnlineListener();
        resolve(true);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(PROJECTS_STORE)) {
          db.createObjectStore(PROJECTS_STORE, { keyPath: 'id' });
        }

        if (!db.objectStoreNames.contains(SYNC_QUEUE_STORE)) {
          db.createObjectStore(SYNC_QUEUE_STORE, { keyPath: 'id', autoIncrement: true });
        }
      };
    });
  }

  /**
   * Save project to offline storage
   */
  async saveProjectOffline(project: OfflineProject): Promise<boolean> {
    if (!this.db) {
      return false;
    }

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([PROJECTS_STORE], 'readwrite');
      const store = transaction.objectStore(PROJECTS_STORE);
      const request = store.put(project);

      request.onerror = () => resolve(false);
      request.onsuccess = () => resolve(true);
    });
  }

  /**
   * A single stored project, by id.
   *
   * The uid-filtered scan below cannot answer this: its callers passed `''`
   * and matched nothing.
   */
  async getOfflineProject(projectId: string): Promise<OfflineProject | null> {
    if (!this.db) {
      return null;
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([PROJECTS_STORE], 'readonly');
      const store = transaction.objectStore(PROJECTS_STORE);
      const request = store.get(projectId);

      // S76: this resolved `null` on error — the same value it returns when nothing
      // was ever stashed. `useEditorState` reads that null as "no draft to recover",
      // renders the older server copy, and the next autosave overwrites the newer
      // local work. A failed IndexedDB read was silently converted into data loss.
      request.onerror = () =>
        reject(request.error ?? new Error('Could not read the offline project store'));
      request.onsuccess = () => resolve((request.result as OfflineProject) ?? null);
    });
  }

  /**
   * Get all offline projects for a user
   */
  async getOfflineProjects(uid: string): Promise<OfflineProject[]> {
    if (!this.db) {
      return [];
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([PROJECTS_STORE], 'readonly');
      const store = transaction.objectStore(PROJECTS_STORE);
      const request = store.getAll();

      // S76: `resolve([])` here meant a failed scan and an empty store were the
      // same answer — "you have nothing saved offline".
      request.onerror = () =>
        reject(request.error ?? new Error('Could not list the offline project store'));
      request.onsuccess = () => {
        const projects = (request.result as OfflineProject[]).filter(p => p.uid === uid);
        resolve(projects);
      };
    });
  }

  /**
   * Remove everything this device holds for one child (S223) — called on sign-out.
   *
   * On a shared classroom Chromebook, sign-out used to clear only in-memory caches: the child's
   * offline project copies, their draft copies and the pair-programming store (`<room>::<uid>`,
   * crdtService) stayed in the browser for whoever used it next. The app's own reads are scoped
   * by uid, but the data was one devtools panel away. Drafts are recovery copies of saves that
   * failed; the caller tries to sync before calling this, and a sign-out is the child saying they
   * are done on this device.
   */
  /**
   * S232: send this child's unsaved drafts to the server before anything local is cleared.
   *
   * Sign-out used to call `syncToCloud`, whose queue nothing fills, and then delete the drafts — so
   * a child whose autosave failed when the Wi-Fi dropped lost their work by signing out. A draft is
   * written only if it is NEWER than the server copy, and removed only once it has landed. Returns
   * how many could not be sent; those stay on the device.
   */
  async pushDrafts(uid: string): Promise<{ pushed: number; failed: number }> {
    if (!uid) return { pushed: 0, failed: 0 };
    const ids = new Set<string>();
    for (const p of await this.getOfflineProjects(uid).catch(() => [] as OfflineProject[])) {
      if (p.syncStatus === 'pending') ids.add(p.id);
    }
    try {
      const prefix = `nsgs_draft_${uid}_`;
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key && key.startsWith(prefix)) ids.add(key.slice(prefix.length));
      }
    } catch {
      // Storage blocked: no drafts there.
    }

    let pushed = 0;
    let failed = 0;
    for (const projectId of ids) {
      const draft = await this.getDraft(projectId, uid);
      if (!draft) continue;
      try {
        const ref = doc(db.value, 'projects', projectId);
        const server = await getDoc(ref);
        const updatedAt = server.get('updatedAt') as { toMillis?: () => number } | undefined;
        const serverMillis = typeof updatedAt?.toMillis === 'function' ? updatedAt.toMillis() : 0;
        if (server.exists() && draft.savedAt > serverMillis) {
          await updateDoc(ref, { html: draft.html, css: draft.css, js: draft.js, updatedAt: serverTimestamp() });
        }
        await this.clearDraft(projectId, uid);
        pushed++;
      } catch (error) {
        logger.warn('Could not send an unsaved draft before sign-out', error);
        failed++;
      }
    }
    return { pushed, failed };
  }

  async clearUserData(uid: string, options: { keepDrafts?: boolean } = {}): Promise<void> {
    if (!uid) return;
    try {
      const prefix = `nsgs_draft_${uid}_`;
      const doomed: string[] = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key && key.startsWith(prefix)) doomed.push(key);
      }
      if (!options.keepDrafts) doomed.forEach((key) => window.localStorage.removeItem(key));
    } catch {
      // Storage blocked: nothing was kept there either.
    }

    // S230: not `.catch(() => [])` — a failed listing would have left this child's copies on the
    // device with nobody told. The caller (logout) logs a failure and still signs out.
    // S232: a draft that could not be sent is the only copy of the child's work; keep it.
    const mine = (await this.getOfflineProjects(uid)).filter(
      (p) => !(options.keepDrafts && p.syncStatus === 'pending')
    );
    if (this.db && mine.length > 0) {
      await new Promise<void>((resolve) => {
        const transaction = this.db!.transaction([PROJECTS_STORE], 'readwrite');
        const store = transaction.objectStore(PROJECTS_STORE);
        mine.forEach((project) => store.delete(project.id));
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => resolve();
      });
    }

    const idb = typeof indexedDB !== 'undefined' ? indexedDB : null;
    if (idb && typeof idb.databases === 'function') {
      const dbs = await idb.databases().catch(() => []);
      dbs
        .filter((d) => typeof d.name === 'string' && d.name.endsWith(`::${uid}`))
        .forEach((d) => idb.deleteDatabase(d.name as string));
    }
  }

  /**
   * Get pending sync queue items
   */
  async getSyncQueue(): Promise<SyncQueueItem[]> {
    if (!this.db) {
      return [];
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([SYNC_QUEUE_STORE], 'readonly');
      const store = transaction.objectStore(SYNC_QUEUE_STORE);
      const request = store.getAll();

      // S76: `resolve([])` made `syncToCloud` return `{synced: 0, failed: 0}` —
      // byte-for-byte the answer it gives when everything is already up to date. A
      // sync indicator driven by that shows "all saved" while the child's queued
      // changes sit unsent, with no error anywhere.
      request.onerror = () =>
        reject(request.error ?? new Error('Could not read the offline sync queue'));
      request.onsuccess = () => resolve(request.result as SyncQueueItem[]);
    });
  }

  /**
   * Add item to sync queue
   */
  async addToSyncQueue(item: Omit<SyncQueueItem, 'id' | 'timestamp' | 'retries'>): Promise<boolean> {
    if (!this.db) {
      return false;
    }

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([SYNC_QUEUE_STORE], 'readwrite');
      const store = transaction.objectStore(SYNC_QUEUE_STORE);
      // A millisecond timestamp alone collided when two saves landed in the
      // same tick, and the store's keyPath is unique — the ConstraintError was
      // swallowed as `false`, silently dropping the queued change.
      const queueItem: SyncQueueItem = {
        id: `${item.projectId}-${Date.now()}-${queueSequence()}`,
        ...item,
        timestamp: new Date(),
        retries: 0
      };

      const request = store.add(queueItem);

      request.onerror = () => resolve(false);
      request.onsuccess = () => resolve(true);
    });
  }

  /**
   * Sync pending changes to cloud
   */
  async syncToCloud(): Promise<{ synced: number; failed: number }> {
    if (!this.isOnline || !this.db) {
      return { synced: 0, failed: 0 };
    }

    const queue = await this.getSyncQueue();
    let synced = 0;
    let failed = 0;

    for (const item of queue) {
      try {
        const projectRef = doc(db.value, 'projects', item.projectId);

        if (item.operation === 'create') {
          // `updatedAt` is REQUIRED, not decorative. `firestore.rules:131` allows a
          // create only when `isValidProject()` holds, and that asks for
          // `keys().hasAll(['title','userId','createdAt','updatedAt'])`; line 134 then
          // asks for `updatedAt == request.time`. Without it every queued create was
          // refused with permission-denied, counted as `failed`, and left in the queue
          // to be refused again on the next reconnect — so a project a child created
          // while the Wi-Fi was down could never reach the server. Found 2026-09-10 by
          // running this service against the live rules; the in-memory double accepted
          // the write. `projectService.createProject` writes both stamps for the same
          // reason (projectService.ts:162).
          await setDoc(projectRef, {
            ...item.data,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            syncedAt: serverTimestamp()
          });
        } else if (item.operation === 'update') {
          await updateDoc(projectRef, {
            ...item.data,
            updatedAt: serverTimestamp(),
            syncedAt: serverTimestamp()
          });
        } else if (item.operation === 'delete') {
          // S76: `delete` is a value `SyncQueueItem` explicitly admits, and it
          // matched NEITHER branch — so it fell straight through to
          // `removeFromSyncQueue` and `synced++`. The queue entry was destroyed, the
          // Firestore document was never deleted, and the caller was told the change
          // had synced. Soft-delete, matching every other deletion path in the
          // product, so nothing is destroyed by a sync that cannot be reviewed.
          await updateDoc(projectRef, {
            isDeleted: true,
            deletedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            syncedAt: serverTimestamp()
          });
        } else {
          // An operation nobody wrote a branch for is a bug, not a success. Counting
          // it as synced is what let `delete` disappear silently for months.
          throw new Error(`Unsupported sync operation: ${String(item.operation)}`);
        }

        // Remove from queue
        await this.removeFromSyncQueue(item.id);
        synced++;

        // Update project sync status. This used to scan with an empty uid,
        // which matched nothing, so the status never changed and a synced
        // draft stayed marked pending forever.
        const project = await this.getOfflineProject(item.projectId);
        if (project) {
          project.syncStatus = 'synced';
          project.lastSyncedAt = new Date();
          await this.saveProjectOffline(project);
        }
      } catch (error) {
        console.error('Sync error:', error);
        failed++;
      }
    }

    return { synced, failed };
  }

  /**
   * Remove item from sync queue
   */
  private async removeFromSyncQueue(id: string): Promise<boolean> {
    if (!this.db) {
      return false;
    }

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([SYNC_QUEUE_STORE], 'readwrite');
      const store = transaction.objectStore(SYNC_QUEUE_STORE);
      const request = store.delete(id);

      request.onerror = () => resolve(false);
      request.onsuccess = () => resolve(true);
    });
  }

  /**
   * Resolve a conflict between the local draft and the server's version.
   *
   * Two outcomes only, both of which actually do something:
   *
   *   'local'  — queue the local copy to be written to Firestore.
   *   'remote' — DISCARD the local copy. It used to be marked `synced` and
   *              left in place, so the stale draft sat there ready to be
   *              written over the server's version on the next sync. Keeping
   *              a copy the user chose to abandon is how you lose the version
   *              they chose to keep.
   *
   * `'merge'` was removed: it also just set `synced` and merged nothing.
   * Merging two versions of a child's game code is not something to fake, and
   * a option that silently does the wrong thing is worse than not offering it.
   */
  async resolveConflict(projectId: string, resolution: 'local' | 'remote'): Promise<boolean> {
    const project = await this.getOfflineProject(projectId);

    if (!project) {
      return false;
    }

    if (resolution === 'remote') {
      return this.clearDraft(projectId);
    }

    await this.addToSyncQueue({
      projectId,
      operation: 'update',
      data: project.localData,
    });
    project.syncStatus = 'pending';
    return this.saveProjectOffline(project);
  }

  /**
   * Store the work-in-progress copy of a project.
   *
   * Called whenever a Firestore save fails. IndexedDB where available,
   * localStorage otherwise — private browsing and some managed-device configs
   * block IDB, and a child on one of those should still not lose their work.
   */
  async saveDraft(
    projectId: string,
    uid: string,
    code: { html: string; css: string; js: string }
  ): Promise<boolean> {
    const draft: OfflineProject = {
      id: projectId,
      name: projectId,
      uid,
      syncStatus: 'pending',
      localData: { ...code, savedAt: Date.now() },
    };

    if (this.db) {
      return this.saveProjectOffline(draft);
    }

    try {
      window.localStorage.setItem(draftKey(projectId, uid), JSON.stringify(draft));
      return true;
    } catch (error) {
      // A quota failure here means the work is only in memory. Say so.
      logger.error('Could not store the offline draft', error);
      return false;
    }
  }

  /**
   * The stored draft for a project, if there is one.
   *
   * Reads IndexedDB first, then localStorage — including the `offline_<id>`
   * key the editor used to write directly, so drafts stashed before this
   * service existed are still recoverable rather than stranded.
   */
  async getDraft(
    projectId: string,
    uid: string
  ): Promise<{ html: string; css: string; js: string; savedAt: number } | null> {
    // S61: the viewer is required, and a draft belonging to somebody else is refused.
    // IndexedDB records are keyed by project id alone, so without this check the next
    // child to open the same project on a shared device got the previous child's work.
    if (!uid) return null;

    const fromDb = await this.getOfflineProject(projectId);
    if (fromDb && fromDb.uid !== uid) return null;
    const parse = (
      data: Record<string, unknown> | undefined
    ): { html: string; css: string; js: string; savedAt: number } | null => {
      if (!data) return null;
      const html = typeof data.html === 'string' ? data.html : undefined;
      const css = typeof data.css === 'string' ? data.css : undefined;
      const js = typeof data.js === 'string' ? data.js : undefined;
      if (html === undefined && css === undefined && js === undefined) return null;
      const savedAt =
        typeof data.savedAt === 'number'
          ? data.savedAt
          : typeof data.timestamp === 'number'
            ? data.timestamp
            : 0;
      return { html: html ?? '', css: css ?? '', js: js ?? '', savedAt };
    };

    const fromIndexedDb = parse(fromDb?.localData);
    if (fromIndexedDb) return fromIndexedDb;

    try {
      const scoped = window.localStorage.getItem(draftKey(projectId, uid));
      // Legacy, UNSCOPED keys: the pre-S61 key and the one the editor wrote before this
      // service existed. Neither carries the viewer in the key, so they are only used
      // when the stored payload itself names this user. A legacy draft with no uid at
      // all cannot be attributed to anyone and is refused rather than shown to whoever
      // happens to be sitting there.
      const legacy =
        window.localStorage.getItem(`nsgs_draft_${projectId}`) ??
        window.localStorage.getItem(`offline_${projectId}`);

      const raw = scoped ?? legacy;
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      if (!scoped && parsed.uid !== uid) return null;
      // Either shape: a full OfflineProject, or the bare code object the
      // editor used to stash.
      return parse(
        (parsed.localData as Record<string, unknown> | undefined) ?? parsed
      );
    } catch {
      return null;
    }
  }

  /** Remove a stored draft once it has been saved or deliberately discarded. */
  async clearDraft(projectId: string, uid = ''): Promise<boolean> {
    try {
      if (uid) window.localStorage.removeItem(draftKey(projectId, uid));

      // S76: the uid-scoped key was removed ONLY when a uid was passed, and five of
      // the six call sites in `useEditorState` pass none — including "Discard" and
      // "Restore". On a device where IndexedDB is blocked (private browsing, some
      // managed Chromebooks) that scoped key is the ONLY copy of the draft, so
      // pressing Discard removed nothing: the same draft was found on the next load
      // and offered back, and restoring it wrote the abandoned code over the
      // server's version and STILL did not clear it, so the prompt recurred with
      // progressively staler code.
      //
      // A project id belongs to exactly one owner, so every `nsgs_draft_*_{id}` key
      // is a draft of this project and all of them should go. Scanning also covers
      // `resolveConflict`, which has no uid to pass.
      const suffix = `_${projectId}`;
      const scoped: string[] = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key && key.startsWith('nsgs_draft_') && key.endsWith(suffix)) {
          scoped.push(key);
        }
      }
      scoped.forEach((key) => window.localStorage.removeItem(key));

      // Legacy unscoped keys are cleared regardless — leaving them would keep an
      // unattributable draft on a shared device (S61).
      window.localStorage.removeItem(`nsgs_draft_${projectId}`);
      window.localStorage.removeItem(`offline_${projectId}`);
    } catch {
      // Nothing to do — a blocked localStorage cannot be holding a draft.
    }

    if (!this.db) return true;

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([PROJECTS_STORE], 'readwrite');
      const store = transaction.objectStore(PROJECTS_STORE);
      const request = store.delete(projectId);
      request.onerror = () => resolve(false);
      request.onsuccess = () => resolve(true);
    });
  }

  /**
   * Get current connection status
   */
  getConnectionStatus(): 'online' | 'offline' {
    return this.isOnline ? 'online' : 'offline';
  }

  /**
   * Get offline storage usage
   */
  /**
   * S76: `used`, `quota` and `percentage` are `null` when the figure is UNKNOWN.
   *
   * All three used to be `0` in three different situations: a browser with no
   * Storage API, an estimate that threw, and a device with nothing stored. The
   * child this screen exists for is the one on a full Chromebook whose drafts are
   * being dropped for lack of space, and they were shown "0% used".
   */
  async getStorageUsage(): Promise<{
    used: number | null;
    quota: number | null;
    percentage: number | null;
  }> {
    if (!navigator.storage?.estimate) {
      // Not an error: this browser genuinely cannot tell us. Say so.
      return { used: null, quota: null, percentage: null };
    }

    try {
      const estimate = await navigator.storage.estimate();
      const used = typeof estimate.usage === 'number' ? estimate.usage : null;
      const quota = typeof estimate.quota === 'number' ? estimate.quota : null;
      return {
        used,
        quota,
        percentage: used !== null && quota ? (used / quota) * 100 : null,
      };
    } catch (error) {
      console.error('Error getting storage estimate:', error);
      return { used: null, quota: null, percentage: null };
    }
  }

  /**
   * Clear all offline data
   */
  async clearOfflineData(): Promise<boolean> {
    if (!this.db) {
      return false;
    }

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([PROJECTS_STORE, SYNC_QUEUE_STORE], 'readwrite');
      const projectsStore = transaction.objectStore(PROJECTS_STORE);
      const queueStore = transaction.objectStore(SYNC_QUEUE_STORE);

      projectsStore.clear();
      queueStore.clear();

      transaction.onerror = () => resolve(false);
      transaction.oncomplete = () => {
        logger.log('Offline data cleared');
        resolve(true);
      };
    });
  }

  /**
   * Setup online/offline listener
   */
  private setupOnlineListener(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('online', () => {
      this.isOnline = true;
      this.syncToCloud();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }
}

export const offlineService = new OfflineService();
